import type { CSSProperties, ReactNode } from "react";

const labelStyle: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 8,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
  fontWeight: 500,
};

const valueStyle: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 16,
  fontVariantNumeric: "tabular-nums",
  fontWeight: 500,
};

function formatUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0,
  });
  return neg ? `−${abs}` : abs;
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 10_000) return `$${(n / 1000).toFixed(2)}K`;
  if (n >= 1000) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

function Tile({
  label,
  children,
  accent,
}: {
  label: string;
  children: ReactNode;
  accent?: string;
}) {
  return (
    <div
      style={{
        padding: "10px 14px 12px",
        borderRight: "1px solid var(--line-1)",
        borderBottom: "1px solid var(--line-1)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 0,
        boxShadow: accent ? `inset 2px 0 0 ${accent}` : undefined,
      }}
    >
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  );
}

export function RiskRewardTiles({
  maxGain,
  maxLoss,
  atMark,
  breakevens,
  strategy,
}: {
  maxGain: number;
  maxLoss: number;
  atMark: number;
  breakevens: ReadonlyArray<number>;
  strategy: string;
}) {
  const rr =
    maxLoss !== 0 && isFinite(maxGain) && isFinite(maxLoss)
      ? Math.abs(maxGain / maxLoss)
      : null;
  const rrBarPct = rr === null ? 0 : Math.min(100, (rr / 4) * 100);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "auto auto",
        overflow: "auto",
      }}
    >
      <Tile label="AT MARK" accent={atMark >= 0 ? "var(--up)" : "var(--dn)"}>
        <span
          style={{
            ...valueStyle,
            color: atMark >= 0 ? "var(--up)" : "var(--dn)",
          }}
        >
          {atMark >= 0 ? "+" : "−"}
          {formatUsd(Math.abs(atMark))}
        </span>
      </Tile>
      <Tile label="MAX GAIN" accent="var(--up)">
        <span style={{ ...valueStyle, color: "var(--up)" }}>
          {formatUsd(maxGain)}
        </span>
      </Tile>
      <Tile label="MAX LOSS" accent="var(--dn)">
        <span style={{ ...valueStyle, color: "var(--dn)" }}>
          −{formatUsd(Math.abs(maxLoss))}
        </span>
      </Tile>

      <Tile label="R : R">
        <span
          style={{
            ...valueStyle,
            color: rr && rr >= 1 ? "var(--up)" : "var(--fg-hi)",
          }}
        >
          {rr !== null ? rr.toFixed(2) : "—"}
        </span>
        <div
          style={{
            height: 3,
            background: "var(--bg-0)",
            borderRadius: 1,
            marginTop: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${rrBarPct}%`,
              height: "100%",
              background:
                rr && rr >= 1 ? "var(--up)" : "rgba(194,214,255,0.5)",
              transition: "width 200ms ease",
            }}
          />
        </div>
      </Tile>

      <Tile label="BREAKEVEN(S)">
        <span
          style={{
            ...valueStyle,
            fontSize: breakevens.length > 1 ? 12 : 16,
            color: "var(--fg-hi)",
          }}
        >
          {breakevens.length === 0
            ? "—"
            : breakevens.map(formatPrice).join(" · ")}
        </span>
      </Tile>

      <Tile label="STRATEGY">
        <span
          style={{
            ...valueStyle,
            fontSize: 13,
            color: "var(--fg-max)",
            letterSpacing: "0.08em",
          }}
        >
          {strategy}
        </span>
      </Tile>
    </div>
  );
}
